import React from "react";
import { ArrowLeft, Ban } from "lucide-react";
import LoadStatusBadge from "./LoadStatusBadge";
import { formatInstantDay } from "../../lib/calendar-date";

/**
 * What a driver sees on a CANCELLED load (fe#815 cancel), in place of the
 * load's steps: no checklist, no Depart / Arrive, no signature, no edit.
 *
 * A cancelled load used to open on its first step with every action live,
 * and the API took them: `depart` set it back to en route and `sign` made it
 * billable. The API now refuses every write to it (409 LOAD_CANCELLED); this
 * is the screen saying so before anyone tries. It stays in the driver's BOL
 * list, marked "Cancelled" (operator, 2026-10-19).
 *
 * The reason is the office's, as typed. A cancel with no reason on file (one
 * from before reasons were required) says so rather than showing a blank.
 */
interface Props {
  bolNumber: string | null | undefined;
  reason: string | null | undefined;
  cancelledAt: string | null | undefined;
  timeZone: string | null | undefined;
  onBack: () => void;
}

export default function CancelledLoadNotice({ bolNumber, reason, cancelledAt, timeZone, onBack }: Props) {
  const day = cancelledAt ? formatInstantDay(cancelledAt, timeZone) : null;
  return (
    <div data-testid="cancelled-load-notice" className="min-h-full bg-fieldiq-dark px-4 py-5">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="grid h-10 w-10 place-items-center rounded-lg border border-fieldiq-border bg-fieldiq-card text-fieldiq-text"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <h1 className="text-lg font-bold text-fieldiq-text">BOL {bolNumber ?? "—"}</h1>
          <LoadStatusBadge status="cancelled" />
        </div>
        <section className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-4">
          <div className="flex items-start gap-3">
            <Ban className="mt-0.5 h-5 w-5 shrink-0 text-fieldiq-text" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-fieldiq-text">This load was cancelled{day ? ` on ${day}` : ""}.</p>
              <p className="text-sm text-fieldiq-text">There is nothing to do on it: it can't be started, delivered or signed.</p>
              <p data-testid="cancelled-load-reason" className="text-sm text-fieldiq-muted">
                Reason: {reason?.trim() ? reason : "none on file"}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
